import * as React from "react";
import { connect } from "react-redux";

import { Form } from "semantic-ui-react";
import { ThunkDispatch } from "redux-thunk";

import { EditComponent, SaveDispatchProps, EditProps as SharedEditProps } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";

import { CurrentUser, CUSTOMER_USER_CASE_TYPES, LinkObject } from "@teamprevent/common/model";
import { CaseTypeOrgStructuresEdit } from "@teamprevent/common/customerUser/CaseTypeOrgStructuresEdit";
import { OrgStructureDetailModel } from "@teamprevent/common/orgStructure/models";
import { ALL_ORG_STRUCTURES, CustomerUserCaseTypeOrgStructures, getEmailDomain } from "@teamprevent/common/customerUser/models";
import { toast } from "@teamprevent/common/toasts";

import { CustomerUserEditModel, AppState } from "../model";
import { customerUserSelector, basePathSelector } from "../selectors";
import { saveCustomerUser } from "../actions";
import { CUSTOMER_ROLES, isCustomerAdministrator, notCaseManagerRole, notAdministratorRole, notAnySraRole } from "../../../auth/model";
import { ApplicationState } from "../../../applicationState";
import { AllowedAssessmentAndWellBeingFeatures } from "../../../assessmentAndWellBeing/models";
import { orgStructuresSelector } from "../../../orgStructure/selectors";
import { CustomerDetailsModel } from "../../../customerDetails/models";

import { EditModal } from "./EditModal";

import { push } from "redux-little-router";

import "../styles/form-dividers.scss";

export interface EditProps extends SharedEditProps<CustomerUserEditModel> {
    open: boolean;
    customer: CustomerDetailsModel;
    onRolesChanged: (newRoles: string[]) => void;
    currentUser: CurrentUser;
    allowedAssessmentAndWellBeingFeatures?: string[];
    orgStructures: OrgStructureDetailModel[];
    onEmailAddressChanged: (emailAddress: string, valid: boolean) => void;
    onCaseTypeOrgStructuresChangesProp: (caseTypeOrgStructures: CustomerUserCaseTypeOrgStructures[], valid: boolean) => void;
}

export interface DispatchProps extends SaveDispatchProps<CustomerUserEditModel> {
    close: () => void;
}

export class EditUnconnected extends EditComponent<CustomerUserEditModel, EditProps> {
    constructor(props: EditProps & SaveDispatchProps<CustomerUserEditModel>) {
        super(props);

        this.onCaseTypeOrgStructuresChanged = this.onCaseTypeOrgStructuresChanged.bind(this);
        this.handleCaseTypeOrgStructuresChange = this.handleCaseTypeOrgStructuresChange.bind(this);
    }

    private onEmailAddressChanged = (value: string, valid: boolean) => {
        this.setState({
            values: { ...this.state.values, emailAddress: value },
            valid: { ...this.state.valid, emailAddress: valid }
        }, () => this.props.onEmailAddressChanged(value, valid));
    };

    private onRolesChanged = (value: any[], valid: boolean) => {
        this.setState({
            values: { ...this.state.values, roles: value },
            valid: { ...this.state.valid, roles: valid }
        }, () => this.props.onRolesChanged(value));
    };

    public componentDidUpdate(prevProps: EditProps) {
        const { handleCaseTypeOrgStructuresChange } = this;
        const prevCaseTypeOrgStructures = prevProps.model.caseTypeOrgStructures;
        const { caseTypeOrgStructures } = this.props.model;
        if (prevCaseTypeOrgStructures && caseTypeOrgStructures) {
            CUSTOMER_USER_CASE_TYPES.forEach(ct => {
                handleCaseTypeOrgStructuresChange(ct.value, prevCaseTypeOrgStructures, caseTypeOrgStructures);
            });
        }
    }

    private handleCaseTypeOrgStructuresChange(
        caseTypeVal: string,
        prevCaseTypeOrgStructures: CustomerUserCaseTypeOrgStructures[],
        caseTypeOrgStructures: CustomerUserCaseTypeOrgStructures[]) {

        const prevCaseStruct = prevCaseTypeOrgStructures.find(p => p.value === caseTypeVal);
        const currCaseStruct = caseTypeOrgStructures.find(c => c.value === caseTypeVal);

        if (prevCaseStruct && currCaseStruct && currCaseStruct.orgStructures.length > 1) {
            if (currCaseStruct.orgStructures[currCaseStruct.orgStructures.length - 1].id === ALL_ORG_STRUCTURES) {
                this.onCaseTypeOrgStructuresChanged(currCaseStruct, [currCaseStruct.orgStructures[currCaseStruct.orgStructures.length - 1]]);
            } else if (currCaseStruct.orgStructures[0].id === ALL_ORG_STRUCTURES) {
                this.onCaseTypeOrgStructuresChanged(currCaseStruct, currCaseStruct.orgStructures.filter(os => os.id !== ALL_ORG_STRUCTURES));
            }
        }
    }

    private onCaseTypeOrgStructuresChanged(
        caseTypeOrgStructure: CustomerUserCaseTypeOrgStructures,
        orgStructures: LinkObject[],
        includeLinkedImmunisationCases?: boolean) {

        const currentCaseTypeOrgStructureIndex = this.state.values.caseTypeOrgStructures.findIndex(ct => ct.value === caseTypeOrgStructure.value);
        const newCaseTypeOrgStructures = [...this.state.values.caseTypeOrgStructures];
        newCaseTypeOrgStructures[currentCaseTypeOrgStructureIndex].orgStructures = orgStructures;
        if (includeLinkedImmunisationCases !== undefined)
        {
            newCaseTypeOrgStructures[currentCaseTypeOrgStructureIndex].includeLinkedImmunisationCases = includeLinkedImmunisationCases;
        }

        this.props.onCaseTypeOrgStructuresChangesProp(newCaseTypeOrgStructures, true);
        return this.updateProperty("caseTypeOrgStructures", newCaseTypeOrgStructures, true);
    }

    private customerRoleOptions = () => {
        return Object.keys(CUSTOMER_ROLES) as (keyof typeof CUSTOMER_ROLES)[];
    };

    public render() {
        return (
            <Form onSubmit={this.handleSubmit}>
                <Input.Email
                    value={this.state.values.emailAddress}
                    label="Email Address *"
                    showErrors={this.state.showErrors}
                    required
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.onEmailAddressChanged(value, valid)}
                />
                <div className={"fields-divider"} />
                <Input.Text
                    value={this.state.values.firstName}
                    label="First Name *"
                    showErrors={this.state.showErrors}
                    required
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("firstName", value, valid)}
                />
                <div className={"fields-divider"} />
                <Input.Text
                    value={this.state.values.surname}
                    label="Surname *"
                    showErrors={this.state.showErrors}
                    required
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("surname", value, valid)}
                />
                <div className={"fields-divider"} />
                <Input.DropdownMulti
                    value={this.state.values.roles}
                    label="Roles *"
                    placeholder="Roles"
                    options={this.customerRoleOptions()
                        .filter(role =>
                            (notCaseManagerRole(role) ||
                                isCustomerAdministrator(this.props.currentUser)) &&
                            notAdministratorRole(role) &&
                            (notAnySraRole(role) ||
                                (this.props.allowedAssessmentAndWellBeingFeatures?.indexOf(AllowedAssessmentAndWellBeingFeatures.SRA) >= 0)))
                        .map(role => ({ key: CUSTOMER_ROLES[role].name, text: CUSTOMER_ROLES[role].displayName, value: CUSTOMER_ROLES[role].name }))}
                    required
                    showErrors={this.state.showErrors}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.onRolesChanged(value, valid)}
                />
                <div className={"fields-divider"} />
                <CaseTypeOrgStructuresEdit
                    caseTypeOrgStructures={this.state.values.caseTypeOrgStructures}
                    orgStructures={this.props.orgStructures}
                    onCaseTypeOrgStructuresChanged={this.onCaseTypeOrgStructuresChanged}
                />
            </Form>
        );
    }

    public submit(): void {
        const emailDomain = getEmailDomain(this.state.values.emailAddress);
        const isEmailWithinAllowedDomains = this.props.customer.allowedEmailDomains?.includes(emailDomain);
        if (!isEmailWithinAllowedDomains) {
            toast.warning("Email address must belong to allowed email domains!");
            return;
        }

        this.handleSubmit({ preventDefault: (): void => undefined } as any);
    }
}

function mapStateToProps(state: AppState & ApplicationState) {
    return {
        model: customerUserSelector(state) as CustomerUserEditModel,
        open: state.router.pathname?.endsWith("/edit"),
        basePath: basePathSelector(state),
        customer: state.customerDetails,
        currentUser: state.currentUser,
        allowedAssessmentAndWellBeingFeatures: state.allowedAssessmentAndWellBeingFeatures,
        orgStructures: orgStructuresSelector(state)
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<AppState, undefined, any>) {
    return {
        dispatchSave: (customerUser: CustomerUserEditModel, basePath: string) => dispatch(saveCustomerUser(customerUser, basePath)),
        dispatchClose: (basePath: string) => dispatch(push(basePath, {}))
    };
}

function mergeProps(propsFromState: any, propsFromDispatch: any): EditProps & DispatchProps {
    return {
        model: propsFromState.model,
        open: propsFromState.open,
        customer: propsFromState.customer,
        onRolesChanged: propsFromState.onRolesChanged,
        save: (customerUser: CustomerUserEditModel) => propsFromDispatch.dispatchSave(customerUser, propsFromState.basePath),
        close: () => propsFromDispatch.dispatchClose(`${propsFromState.basePath}/${propsFromState.model.id}`),
        currentUser: propsFromState.currentUser,
        allowedAssessmentAndWellBeingFeatures: propsFromState.allowedAssessmentAndWellBeingFeatures,
        orgStructures: propsFromState.orgStructures,
        onEmailAddressChanged: propsFromState.onEmailAddressChanged,
        onCaseTypeOrgStructuresChangesProp: propsFromState.onCaseTypeOrgStructuresChangesProp
    };
}

export const Edit = connect(mapStateToProps, mapDispatchToProps, mergeProps)(EditModal);
